import {
  CommonService,
  Exception,
  StorageService,
  StorageType,
  ToasterService,
} from '@anddone/ui-utilities';
import { ChangeDetectorRef, Component, signal, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { LoginService } from '../../../../../services/login-service';
import { UserService } from '../../../../../services/user-service';
import { AllyCommonService } from '../../../../../services/ally-common-service';
import { UserAuthenticationType } from '../../../../../enum/UserAuthenticationType.enum';
import { RollApi } from '../roll-api/roll-api';
import { MessageConstants } from '../../../../../constants/message.constant';
import { Loader } from '../../../../layout/loader/loader';
import { DatePipe, NgClass } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { EditUser } from '../edit-user/edit-user';

@Component({
  selector: 'app-users-details',
  imports: [Loader, NgClass, DatePipe, TranslateModule, NgbModalModule],
  templateUrl: './users-details.html',
  styleUrl: './users-details.scss',
})
export class UsersDetails implements OnInit {
  //Variables
  loggedInUserData: any = {};
  name!: string;
  userName = '';
  isActive!: boolean;
  modifiedOn = '';
  userEmail = '';
  phoneNumber: any;
  userAuthenticationType: any;
  address!: string;
  isLoader = signal<boolean>(false);
  userId!: string;
  showKey = false;
  showOldKey = false;
  apiModalRef: any;
  editModalRef: any;
  oldAPIKey: any = null;
  newAPIKey: any = null;
  isUserProfile!: boolean;
  private destroy$ = new Subject<void>();
  isApiKeyUser = false;

  private storageService = inject(StorageService);
  private commonService = inject(CommonService);
  public allyCommonService = inject(AllyCommonService);
  private activatedRouter = inject(ActivatedRoute);
  private toasterService = inject(ToasterService);
  private userService = inject(UserService);
  private loginService = inject(LoginService);
  private router = inject(Router);
  public modalService = inject(NgbModal);
  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    this.isLoader.set(true);
    this.showKey = false;
    this.showOldKey = false;
    // to get logged in user details
    this.loggedInUserData = this.allyCommonService.getLoggedInData() || '';
    this.activatedRouter.params.subscribe((params) => {
      this.userId = params['id'];
    });

    this.getUserDetails();
    this.userService.setAPIKey(null);
    this.userService.updateRollAPIKeyValue(false);

    this.userService.updatedRollAPIKey.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status === true) {
        this.getAPIKeys();
      }
    });
    this.userService.currentUserDetailUpdate.pipe(takeUntil(this.destroy$)).subscribe((status) => {
      if (status === true) {
        this.getUserDetails();
      }
    });
  }

  getUserDetails() {
    this.isUserProfile = this.router.url.includes('user-profile');
    if (!this.userId) return;

    const reqObj = { allyId: this.loggedInUserData?.id, userId: this.userId };

    this.userService.getUserDetailsById(reqObj).subscribe({
      next: (res: any) => {
        this.userService.setUserDetails(res);

        this.isApiKeyUser = ![UserAuthenticationType.BearerOnly, 1].includes(
          res.userAuthenticationType
        );
        if (this.isApiKeyUser && res.isActive) {
          this.getAPIKeys();
        }

        const {
          contact = {},
          userName,
          userAuthenticationType,
          isActive,
          changedPasswordOn,
          id,
        } = res;
        const { name = {}, email, phone, address = {} } = contact;

        this.name = `${name.firstName || ''} ${name.lastName || ''}`.trim();
        this.userName = userName;
        this.userEmail = email;
        this.phoneNumber = phone;
        this.userAuthenticationType = userAuthenticationType;
        this.isActive = isActive;
        this.modifiedOn = changedPasswordOn;

        const countryMap: any = { '1': 'USA', '2': 'CANADA' };
        this.address =
          [
            address.addressLine1,
            address.addressLine2,
            address.city,
            address.state,
            address.postalCode,
            countryMap[address.country],
          ]
            .filter(Boolean)
            .join(', ') || '--';

        this.isLoader.set(false);

        if (id === this.commonService.nonDefaultAdminUserId) {
          this.storageService.save(StorageType.Session, 'nonDefaultAdminUser', JSON.stringify(res));
        }
      },
      error: (err) => {
        const msg = Exception.exceptionMessage(err).join(', ');
        this.toasterService.showError(msg, 'full');
        this.isLoader.set(false);
      },
    });
  }

  getAPIKeys() {
    if (!this.userId) return;
    this.isLoader.set(true);
    const reqObj = {
      allyId: this.loggedInUserData?.id,
      userId: this.userId,
    };

    this.userService.getKeys(reqObj).subscribe({
      next: (response: any) => {
        if (response.length !== 0) {
          this.newAPIKey = response[0].keyValue;
          this.oldAPIKey = response[0].oldKeyValue;
        } else {
          this.newAPIKey = null;
          this.oldAPIKey = null;
        }

        this.userService.setAPIKey(response);
        this.cdr.detectChanges();
        this.isLoader.set(false);
      },
      error: (error: any) => {
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoader.set(false);
      },
    });
  }

  //function to activate user
  userActivate() {
    this.isLoader.set(true);
    this.userService.activateUser(this.loggedInUserData?.id, this.userId).subscribe({
      next: (_response: any) => {
        if (this.isApiKeyUser == true) {
          this.getAPIKeys();
        }
        this.toasterService.showSuccess(MessageConstants.common.userActivate, 'full');
        this.isActive = true;
        this.isLoader.set(false);
      },
      error: (error: any) => {
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoader.set(false);
      },
    });
  }

  //function to deactivate User
  userDeactivate() {
    this.isLoader.set(true);
    this.userService.deactivateUser(this.loggedInUserData?.id, this.userId).subscribe({
      next: (_response: any) => {
        this.toasterService.showSuccess(MessageConstants.common.userDeactivated, 'full');
        this.isActive = false;
        this.isLoader.set(false);
      },
      error: (error: any) => {
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoader.set(false);
      },
    });
  }

  // Change User Password
  changePassword() {
    this.isLoader.set(true);
    const reqObj = {
      allyId: this.loggedInUserData?.id,
      userName: this.userName,
      data: { isReset: true },
    };

    this.loginService.changePassword(reqObj).subscribe({
      next: (_response: any) => {
        this.toasterService.showSuccess(MessageConstants.forgotPassword.passwordSent, 'full');
        this.isLoader.set(false);
      },
      error: (error: any) => {
        const toastMessage = Exception.exceptionMessage(error);
        this.toasterService.showError(toastMessage.join(', '), 'full');
        this.isLoader.set(false);
      },
    });
  }

  formatPhoneNumber(phone: string): string {
    const digits = phone?.replace(/\D/g, '');
    return `(${digits?.slice(0, 3)}) ${digits?.slice(3, 6)}-${digits?.slice(6, 10)}`;
  }

  backToUsers() {
    this.router.navigate(['/ally/users']);
  }

  routeToChangePassword() {
    this.router.navigate(['/ally/profile/change-password']);
  }

  toggleApiKey() {
    this.showKey = !this.showKey;
  }

  toggleOldApiKey() {
    this.showOldKey = !this.showOldKey;
  }

  showApiModal() {
    this.apiModalRef = this.modalService.open(RollApi, { scrollable: true });
  }

  showEditModal() {
    this.editModalRef = this.modalService.open(EditUser, { scrollable: true });
  }
}
